(function campusPerksPopupModule (globalScope) {
  'use strict'

  const CAMPAIGN_PARAM = 'campaign'
  const CAMPAIGN_VALUE = 'Campus-Perks'
  const USER_SOURCE_ID_PARAM = 'sv_campaign_id'
  const REGISTRATION_SUCCESS_PARAM = 'campus_perks_registered'
  const CAMPUS_PERKS_API = 'https://informationcollection.benyuan1.com/api/campusPerks/submit'
  const ACTIVITY_USER_API = 'http://informationcollection.benyuan1.com/api/activityUser/save'
  const CAMPUS_PERKS_API_TIMEOUT_MS = 15000
  let addressPlacesLoading = null
  const API_SCHOOL_BY_IPEDS = Object.freeze({
    110662: 'UCLA',
    170976: 'Michigan',
    193900: 'NYU',
    243780: 'Purdue',
  })
  const VIEW_SELECTORS = Object.freeze({
    offer: '[data-campus-perks-main-view]',
    register: '[data-campus-perks-register-view]',
    success: '[data-campus-perks-success-view]',
    profile: '[data-campus-perks-profile-view]',
    'profile-success': '[data-campus-perks-profile-success-view]',
  })
  const FOCUSABLE_SELECTOR = [
    'button:not([disabled]):not([hidden])',
    'input:not([disabled]):not([type="hidden"]):not([hidden])',
    'textarea:not([disabled]):not([hidden])',
    'select:not([disabled]):not([hidden])',
    'a[href]:not([hidden])',
    '[tabindex]:not([tabindex="-1"]):not([hidden])',
  ].join(',')

  function matchesCampaign (search) {
    try {
      return new URLSearchParams(search || '').get(CAMPAIGN_PARAM) === CAMPAIGN_VALUE
    } catch (error) {
      return false
    }
  }

  function getUserSourceId (search) {
    try {
      return new URLSearchParams(search || '').get(USER_SOURCE_ID_PARAM) || ''
    } catch (error) {
      return ''
    }
  }

  function matchesRegistrationSuccess (search) {
    try {
      return new URLSearchParams(search || '').get(REGISTRATION_SUCCESS_PARAM) === '1'
    } catch (error) {
      return false
    }
  }

  function storefrontConfig (globalRef) {
    const shared = globalRef?.globalFn?.storefrontApi
    const root = globalRef?.Shopify?.routes?.root || '/'
    return {
      endpoint: `${root}api/2026-07/graphql.json`,
      token: shared?.token || '68fc996c788d6ff0a40aea26f65267d3',
    }
  }

  async function createStorefrontCustomer (email, password, globalRef = globalScope) {
    const { endpoint, token } = storefrontConfig(globalRef)
    const response = await globalRef.fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: `
          mutation customerCreate($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
              customer { id }
              customerUserErrors { field message code }
            }
          }
        `,
        // Subscription and its customer tags are applied by the native newsletter
        // form after the account exists, so this creation step stays opt-out.
        variables: { input: { email, password, acceptsMarketing: false } },
      }),
    })

    if (!response.ok) throw new Error('Unable to create the account. Please try again.')
    return response.json()
  }

  async function subscribeCampusPerksCustomer (email, root, globalRef = globalScope) {
    const form = root?.parentElement?.querySelector?.('[data-campus-perks-subscription-form]')
    const emailInput = form?.querySelector?.('[data-campus-perks-subscription-email]')
    if (!form || !emailInput) throw new Error('Unable to enroll your account in Campus Perks. Please try again.')

    emailInput.value = email
    const postUrl = form.getAttribute('action') || form.action
    const response = await globalRef.fetch(postUrl, {
      method: 'POST',
      body: new globalRef.FormData(form),
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })

    // Keep the site's established 422 handling for an existing subscriber, but
    // surface every other failed response to the independent enrollment logger.
    if (!response || (!response.ok && response.status !== 422)) {
      throw new Error('Your account was created, but Campus Perks enrollment failed. Please try again.')
    }
    return response
  }

  async function saveCampusPerksActivityUser (email, globalRef = globalScope) {
    const response = await globalRef.fetch(ACTIVITY_USER_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        activityName: '返校季',
        activityTagList: ['newsletter', 'Campus Perks'],
      }),
    })

    if (!response?.ok) throw new Error('Unable to save Campus Perks activity user.')
    return response
  }

  async function loginCampusPerksCustomer (email, password, root, globalRef = globalScope) {
    const form = root?.parentElement?.querySelector?.('[data-campus-perks-login-form]')
    const emailInput = form?.querySelector?.('[data-campus-perks-login-email]')
    const passwordInput = form?.querySelector?.('[data-campus-perks-login-password]')
    if (!form || !emailInput || !passwordInput) throw new Error('Unable to sign in. Please use the account icon.')

    emailInput.value = email
    passwordInput.value = password
    const postUrl = form.getAttribute('action') || form.action
    const response = await globalRef.fetch(postUrl, {
      method: 'POST',
      body: new globalRef.FormData(form),
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })

    const responsePath = response?.url
      ? new URL(response.url, globalRef?.location?.origin || 'https://shop.invalid').pathname
      : ''
    if (!response?.ok || responsePath.endsWith('/account/login')) {
      throw new Error('Unable to sign in. Please use the account icon.')
    }
    return response
  }

  async function copyText (text, documentRef, navigatorRef) {
    if (navigatorRef?.clipboard?.writeText) {
      try {
        await navigatorRef.clipboard.writeText(text)
        return true
      } catch (error) {
        // Continue to the legacy selection-based copy path.
      }
    }

    if (!documentRef?.body || !documentRef.createElement || !documentRef.execCommand) return false

    const textarea = documentRef.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    documentRef.body.appendChild(textarea)

    try {
      textarea.select()
      return documentRef.execCommand('copy')
    } catch (error) {
      return false
    } finally {
      documentRef.body.removeChild(textarea)
    }
  }

  function normalizeSearch (value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  }

  function normalizeOptionValue (value) {
    return String(value || '').trim().toLowerCase()
  }

  function searchColleges (colleges, query, limit = 8) {
    const normalizedQuery = normalizeSearch(query)
    if (!normalizedQuery || !Array.isArray(colleges)) return []

    const aliases = {
      110662: 'ucla uc los angeles',
      170976: 'michigan umich university of michigan',
      193900: 'nyu new york university',
      243780: 'purdue purdue university',
    }

    return colleges
      .map((college) => {
        const name = normalizeSearch(college?.n)
        const place = normalizeSearch(`${college?.c || ''} ${college?.s || ''}`)
        const alias = aliases[String(college?.i)] || ''
        let score = Number.POSITIVE_INFINITY
        if (alias.split(' ').includes(normalizedQuery) || alias.startsWith(normalizedQuery)) score = 0
        else if (name.startsWith(normalizedQuery)) score = 1
        else if (name.split(' ').some((word) => word.startsWith(normalizedQuery))) score = 2
        else if (name.includes(normalizedQuery)) score = 3
        else if (place.includes(normalizedQuery)) score = 4
        return { college, score }
      })
      .filter((match) => Number.isFinite(match.score))
      .sort((left, right) => left.score - right.score
        || String(left.college.n).localeCompare(String(right.college.n))
        || String(left.college.c).localeCompare(String(right.college.c)))
      .slice(0, limit)
      .map((match) => match.college)
  }

  function mapCollegeToApiSchool (college) {
    const school = API_SCHOOL_BY_IPEDS[String(college?.i)]
    if (school) return { school, schoolOther: '' }
    return { school: 'Other', schoolOther: String(college?.n || '').trim() }
  }

  function updateExclusiveSelection (currentValues, value, checked) {
    const values = Array.isArray(currentValues) ? [...currentValues] : []
    const isNone = String(value).toLowerCase() === 'none'
    if (!checked) return values.filter((item) => item !== value)
    if (isNone) return [value]
    const withoutNone = values.filter((item) => String(item).toLowerCase() !== 'none')
    if (!withoutNone.includes(value)) withoutNone.push(value)
    return withoutNone
  }

  function includesOther (value) {
    const values = Array.isArray(value) ? value : [value]
    return values.some((item) => normalizeOptionValue(item) === 'other')
  }

  function getMissingOtherFields (profile) {
    const rules = [
      ['school', 'schoolOther'],
      ['campusOrganizations', 'campusOrganizationsOther'],
      ['nailReasons', 'nailReasonsOther'],
      ['nailInspirationSources', 'nailInspirationSourcesOther'],
      ['interestProducts', 'interestProductsOther'],
      ['hearAboutProgramSource', 'hearAboutProgramSourceOther'],
      ['futureCampusPrograms', 'futureCampusProgramsOther'],
    ]
    return rules
      .filter(([answer, detail]) => includesOther(profile?.[answer]) && !String(profile?.[detail] || '').trim())
      .map(([, detail]) => detail)
  }

  async function submitCampusProfile (profile, globalRef = globalScope) {
    const AbortControllerConstructor = globalRef?.AbortController
    const controller = AbortControllerConstructor ? new AbortControllerConstructor() : null
    const timeout = controller && globalRef?.setTimeout
      ? globalRef.setTimeout(() => controller.abort(), CAMPUS_PERKS_API_TIMEOUT_MS)
      : null
    let response
    try {
      response = await globalRef.fetch(CAMPUS_PERKS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
        ...(controller ? { signal: controller.signal } : {}),
      })
    } catch (error) {
      if (error?.name === 'AbortError') throw new Error('The request timed out. Please try again.')
      throw error
    } finally {
      if (timeout !== null) globalRef.clearTimeout?.(timeout)
    }

    let payload = {}
    try {
      payload = await response.json()
    } catch (error) {
      // A non-JSON error response is handled below with the HTTP status.
    }

    const successPayload = payload?.data && typeof payload.data === 'object' ? payload.data : payload
    const discountCode = String(successPayload?.discountCode || '').trim()
    const explicitSuccess = successPayload?.success === true
    if (!response.ok || !explicitSuccess || !discountCode) {
      const message = payload?.message || successPayload?.message || payload?.error || `Unable to submit your Campus Profile (${response.status || 'network error'}).`
      throw new Error(message)
    }
    if (payload?.data && typeof payload.data === 'object') {
      return {
        ...payload,
        discountCode,
        message: payload?.message || successPayload?.message || '',
      }
    }
    return payload
  }

  function parseShopifyCustomerId (value) {
    const match = String(value || '').match(/(?:^|\/)(\d+)$/)
    return match ? Number(match[1]) : null
  }

  function loadAddressPlaces (globalRef = globalScope, documentRef = globalRef?.document) {
    if (globalRef?.google?.maps?.places?.Autocomplete) return Promise.resolve()
    if (addressPlacesLoading) return addressPlacesLoading
    addressPlacesLoading = new Promise((resolve, reject) => {
      let script = documentRef.querySelector('script[data-rbf-google-maps], script[src*="maps.googleapis.com/maps/api/js"]')
      const existing = Boolean(script)
      if (!script) {
        script = documentRef.createElement('script')
        // Reuse the public browser key and script marker used by wedding/recycling.
        script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDdfF9e7lZNUT_9QpGLYw2Dv1Nom_nD2C4&libraries=places&v=weekly&language=en'
        script.async = true
        script.setAttribute('data-rbf-google-maps', '')
      }
      let settled = false
      const timeout = globalRef.setTimeout(() => finish(false), 15000)
      const finish = (available) => {
        if (settled) return
        settled = true
        globalRef.clearTimeout(timeout)
        script.removeEventListener('load', loaded)
        script.removeEventListener('error', failed)
        if (available) resolve()
        else {
          if (!existing) script.remove()
          reject(new Error('Address suggestions unavailable'))
        }
      }
      const loaded = async () => {
        script.setAttribute('data-loaded', 'true')
        try {
          if (!globalRef.google?.maps?.places?.Autocomplete && globalRef.google?.maps?.importLibrary) {
            await globalRef.google.maps.importLibrary('places')
          }
          finish(Boolean(globalRef.google?.maps?.places?.Autocomplete))
        } catch (error) {
          finish(false)
        }
      }
      const failed = () => finish(false)
      script.addEventListener('load', loaded, { once: true })
      script.addEventListener('error', failed, { once: true })
      if (globalRef.google?.maps?.importLibrary || script.getAttribute('data-loaded') === 'true') void loaded()
      else if (!existing) documentRef.head.appendChild(script)
    }).catch((error) => {
      addressPlacesLoading = null
      throw error
    })
    return addressPlacesLoading
  }

  class CampusPerksPopupController {
    constructor(root, dependencies = {}) {
      this.root = root
      this.windowRef = dependencies.windowRef || globalScope
      this.documentRef = dependencies.documentRef || globalScope?.document
      this.navigatorRef = dependencies.navigatorRef || globalScope?.navigator
      this.setTimeoutRef = dependencies.setTimeoutRef || globalScope?.setTimeout?.bind(globalScope)
      this.connected = false
      this.opened = false
      this.previousFocus = null
      this.previousBodyOverflow = ''
      this.registering = false
      this.profileSubmitting = false
      this.registeredEmail = ''
      this.registeredCustomerId = ''
      this.colleges = null
      this.collegesPromise = null
      this.visibleCollegeResults = []
      this.selectedCollege = null
      this.activeCollegeIndex = -1
      this.addressAutocomplete = null
      this.addressListener = null
      this.addressLoading = false
      this.addressConnectionId = 0
      this.addressEscapeEvent = null
      this.addressMenu = null
      this.addressMenuObserver = null
      this.addressExistingMenus = null
      this.handleClick = this.handleClick.bind(this)
      this.handleKeydown = this.handleKeydown.bind(this)
      this.handleSubmit = this.handleSubmit.bind(this)
      this.handleInput = this.handleInput.bind(this)
      this.handleChange = this.handleChange.bind(this)
      this.handleAddressFocus = this.handleAddressFocus.bind(this)
      this.handleAddressKeydown = this.handleAddressKeydown.bind(this)
      this.hideAddressSuggestions = this.hideAddressSuggestions.bind(this)
    }

    connect () {
      if (this.connected) return
      this.connected = true
      this.addressConnectionId += 1
      this.root.addEventListener('click', this.handleClick)
      this.root.addEventListener('submit', this.handleSubmit)
      this.root.addEventListener('input', this.handleInput)
      this.root.addEventListener('change', this.handleChange)
      this.root.addEventListener('focusin', this.handleAddressFocus)
      this.root.addEventListener('keydown', this.handleAddressKeydown, true)
      this.root.querySelector('[data-campus-perks-profile-scroll]')?.addEventListener('scroll', this.hideAddressSuggestions, { passive: true })
      this.windowRef?.addEventListener?.('resize', this.hideAddressSuggestions)
      this.documentRef?.addEventListener('keydown', this.handleKeydown)
      if (matchesCampaign(this.windowRef?.location?.search)) this.open()
    }

    disconnect () {
      if (!this.connected) return
      this.hideAddressSuggestions({ blurInput: true })
      this.root.removeEventListener('click', this.handleClick)
      this.root.removeEventListener('submit', this.handleSubmit)
      this.root.removeEventListener('input', this.handleInput)
      this.root.removeEventListener('change', this.handleChange)
      this.root.removeEventListener('focusin', this.handleAddressFocus)
      this.root.removeEventListener('keydown', this.handleAddressKeydown, true)
      this.root.querySelector('[data-campus-perks-profile-scroll]')?.removeEventListener('scroll', this.hideAddressSuggestions)
      this.windowRef?.removeEventListener?.('resize', this.hideAddressSuggestions)
      this.addressListener?.remove()
      this.addressListener = null
      this.addressMenuObserver?.disconnect()
      this.addressLoading = false
      this.addressConnectionId += 1
      this.documentRef?.removeEventListener('keydown', this.handleKeydown)
      this.connected = false
      if (this.opened) this.close()
    }

    open () {
      if (this.opened) return
      this.opened = true
      this.previousFocus = this.documentRef?.activeElement || null
      this.previousBodyOverflow = this.documentRef?.body?.style?.overflow || ''
      this.root.hidden = false
      this.root.setAttribute('aria-hidden', 'false')
      if (this.documentRef?.body?.style) this.documentRef.body.style.overflow = 'hidden'
      if (matchesRegistrationSuccess(this.windowRef?.location?.search)) this.showSuccess()
      else this.showOffer()
      this.root.querySelector('[data-campus-perks-dialog]')?.focus()
    }

    showView (viewName, focusSelector) {
      if (viewName !== 'profile') this.hideAddressSuggestions({ blurInput: true })
      Object.entries(VIEW_SELECTORS).forEach(([name, selector]) => {
        const view = this.root.querySelector(selector)
        if (view) view.hidden = name !== viewName
      })
      this.root.setAttribute('data-campus-perks-view', viewName)
      const dialog = this.root.querySelector('[data-campus-perks-dialog]')
      if (dialog) {
        const activeView = this.root.querySelector(VIEW_SELECTORS[viewName])
        const title = activeView?.querySelector('[data-campus-perks-view-title]')
        const description = activeView?.querySelector('[data-campus-perks-view-description]')
        if (title?.id) dialog.setAttribute('aria-labelledby', title.id)
        else dialog.removeAttribute('aria-labelledby')
        if (description?.id) dialog.setAttribute('aria-describedby', description.id)
        else dialog.removeAttribute('aria-describedby')
      }
      if (focusSelector) this.setTimeoutRef?.(() => this.root.querySelector(focusSelector)?.focus(), 0)
    }

    showOffer () {
      this.showView('offer')
    }

    showRegistration () {
      this.showView('register', '[data-campus-perks-register-email]')
    }

    showSuccess () {
      this.showView('success', '[data-campus-perks-success-view] [data-campus-perks-view-title]')
    }

    showProfile () {
      this.showView('profile', '[data-campus-perks-profile-form] input[name="firstName"]')
      const scrollArea = this.root.querySelector('[data-campus-perks-profile-scroll]')
      if (scrollArea) scrollArea.scrollTop = 0

      const email = this.root.querySelector('[data-campus-perks-profile-email]')
      if (email && !email.value && this.registeredEmail) email.value = this.registeredEmail
      this.updateProfileConditionalFields()
      this.loadColleges().catch((error) => this.showProfileStatus(error?.message || 'Unable to load the school directory.'))
    }

    showRegisterError (message) {
      const errorBox = this.root.querySelector('[data-campus-perks-register-errors]')
      if (!errorBox) return
      errorBox.textContent = message
      errorBox.hidden = !message
    }

    syncLoggedInAccountLinks () {
      const accountUrl = this.root?.dataset?.accountUrl
      if (!accountUrl) return

      this.documentRef?.querySelectorAll?.('.account-button').forEach((link) => {
        link.setAttribute?.('href', accountUrl)
      })
    }

    async registerCustomer () {
      if (this.registering) return
      const emailInput = this.root.querySelector('[data-campus-perks-register-email]')
      const passwordInput = this.root.querySelector('[data-campus-perks-register-password]')
      const submit = this.root.querySelector('[data-campus-perks-register-submit]')
      const email = emailInput?.value?.trim() || ''
      const password = passwordInput?.value || ''

      this.showRegisterError('')
      if (!emailInput?.checkValidity?.()) {
        emailInput?.reportValidity?.()
        return
      }
      if (!passwordInput?.checkValidity?.()) {
        passwordInput?.reportValidity?.()
        return
      }

      this.registering = true
      if (submit) {
        submit.dataset.defaultLabel = submit.dataset.defaultLabel || submit.textContent
        submit.disabled = true
        submit.setAttribute('aria-busy', 'true')
        submit.textContent = 'Registering…'
      }

      try {
        const payload = await createStorefrontCustomer(email, password, this.windowRef)
        const graphError = payload?.errors?.[0]?.message
        const createPayload = payload?.data?.customerCreate
        const customerError = createPayload?.customerUserErrors?.[0]?.message
        if (graphError || customerError) {
          this.showRegisterError(graphError || customerError)
          return
        }
        if (!createPayload?.customer?.id) {
          this.showRegisterError('Could not create account. Please try again.')
          return
        }
        this.registeredEmail = email
        this.registeredCustomerId = createPayload.customer.id
        this.showSuccess()
        void saveCampusPerksActivityUser(email, this.windowRef)
          .catch((error) => console.warn('Campus Perks activity-user collection failed:', error))
        void subscribeCampusPerksCustomer(email, this.root, this.windowRef)
          .catch((error) => console.warn('Campus Perks newsletter subscription failed:', error))
        void loginCampusPerksCustomer(email, password, this.root, this.windowRef)
          .then(() => this.syncLoggedInAccountLinks())
          .catch((error) => console.warn('Campus Perks automatic sign-in failed:', error))
      } catch (error) {
        this.showRegisterError(error?.message || 'Network error. Please try again.')
      } finally {
        this.registering = false
        if (submit) {
          submit.disabled = false
          submit.removeAttribute('aria-busy')
          submit.textContent = submit.dataset.defaultLabel
        }
      }
    }

    async loadColleges () {
      if (this.colleges) return this.colleges
      if (this.collegesPromise) return this.collegesPromise
      const url = this.root.dataset.collegesUrl
      if (!url) throw new Error('The school directory is not configured.')

      this.collegesPromise = this.windowRef.fetch(url, { credentials: 'same-origin' })
        .then(async (response) => {
          if (!response.ok) throw new Error('Unable to load the school directory.')
          const records = await response.json()
          if (!Array.isArray(records)) throw new Error('The school directory is unavailable.')
          this.colleges = records
          return records
        })
        .catch((error) => {
          this.collegesPromise = null
          throw error
        })
      return this.collegesPromise
    }

    async updateSchoolMatches (query) {
      try {
        const colleges = await this.loadColleges()
        const input = this.root.querySelector('[data-campus-perks-school]')
        if (!input || input.value.trim() !== query.trim()) return
        this.renderCollegeOptions(searchColleges(colleges, query))
      } catch (error) {
        this.showProfileStatus(error?.message || 'Unable to load the school directory.')
      }
    }

    renderCollegeOptions (results) {
      const list = this.root.querySelector('[data-campus-perks-school-options]')
      const input = this.root.querySelector('[data-campus-perks-school]')
      if (!list || !input) return

      list.replaceChildren()
      this.visibleCollegeResults = results
      this.activeCollegeIndex = -1

      if (!results.length) {
        const empty = this.documentRef.createElement('li')
        empty.className = 'campus-perks-popup__school-empty'
        empty.textContent = input.value.trim() ? 'No matching schools found.' : 'Start typing to search schools.'
        list.appendChild(empty)
      } else {
        results.forEach((college, index) => {
          const item = this.documentRef.createElement('li')
          item.id = `${list.id}-option-${index}`
          item.setAttribute('role', 'option')
          item.setAttribute('aria-selected', 'false')
          item.dataset.campusPerksCollegeOption = String(college.i)

          const name = this.documentRef.createElement('span')
          name.className = 'campus-perks-popup__school-name'
          name.textContent = college.n
          const location = this.documentRef.createElement('span')
          location.className = 'campus-perks-popup__school-location'
          location.textContent = `${college.c}, ${college.s}`
          item.append(name, location)
          list.appendChild(item)
        })
      }

      list.hidden = false
      input.setAttribute('aria-expanded', 'true')
    }

    hideCollegeOptions () {
      const list = this.root.querySelector('[data-campus-perks-school-options]')
      const input = this.root.querySelector('[data-campus-perks-school]')
      if (list) list.hidden = true
      if (input) {
        input.setAttribute('aria-expanded', 'false')
        input.removeAttribute('aria-activedescendant')
      }
      this.activeCollegeIndex = -1
    }

    selectCollege (college) {
      if (!college) return
      this.selectedCollege = college
      const input = this.root.querySelector('[data-campus-perks-school]')
      if (input) {
        input.value = college.n
        input.setCustomValidity?.('')
      }
      this.hideCollegeOptions()
      this.showProfileStatus('')
      input?.focus?.({ preventScroll: true })
    }

    setActiveCollegeIndex (nextIndex) {
      const options = Array.from(this.root.querySelectorAll('[data-campus-perks-college-option]'))
      if (!options.length) return
      this.activeCollegeIndex = (nextIndex + options.length) % options.length
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeCollegeIndex)))
      const active = options[this.activeCollegeIndex]
      this.root.querySelector('[data-campus-perks-school]')?.setAttribute('aria-activedescendant', active.id)
      active.scrollIntoView?.({ block: 'nearest' })
    }

    async handleSchoolKeydown (event, schoolInput) {
      const list = this.root.querySelector('[data-campus-perks-school-options]')
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault?.()
        if (list?.hidden && schoolInput.value.trim()) await this.updateSchoolMatches(schoolInput.value.trim())
        const isFirstMove = this.activeCollegeIndex < 0
        const nextIndex = isFirstMove
          ? (event.key === 'ArrowDown' ? 0 : this.visibleCollegeResults.length - 1)
          : this.activeCollegeIndex + (event.key === 'ArrowDown' ? 1 : -1)
        this.setActiveCollegeIndex(nextIndex)
        return true
      }
      if (event.key === 'Enter' && this.activeCollegeIndex >= 0) {
        event.preventDefault?.()
        this.selectCollege(this.visibleCollegeResults[this.activeCollegeIndex])
        return true
      }
      if (event.key === 'Escape' && list && !list.hidden) {
        event.preventDefault?.()
        this.hideCollegeOptions()
        return true
      }
      return false
    }

    resolveTypedCollege () {
      if (this.selectedCollege) return this.selectedCollege
      const typedName = normalizeSearch(this.root.querySelector('[data-campus-perks-school]')?.value)
      if (!typedName || !this.colleges) return null
      const exact = this.colleges.filter((college) => normalizeSearch(college.n) === typedName)
      if (exact.length === 1) this.selectedCollege = exact[0]
      return this.selectedCollege
    }

    updateProfileConditionalFields () {
      this.root.querySelectorAll('[data-campus-perks-other-field]').forEach((field) => {
        const detailName = field.dataset.campusPerksOtherField
        const controls = this.root.querySelectorAll(`[data-campus-perks-other-control="${detailName}"]`)
        const visible = Array.from(controls).some((control) => control.checked)
        field.hidden = !visible
        const detailInput = field.querySelector('input, textarea')
        if (detailInput) {
          detailInput.required = visible
          if (!visible) detailInput.value = ''
        }
      })

      const futurePrograms = this.root.querySelectorAll('input[name="futureCampusPrograms"]:checked')
      const community = this.root.querySelector('[data-campus-perks-community]')
      if (community) community.hidden = futurePrograms.length === 0
    }

    handleAddressFocus (event) {
      if (event.target?.matches?.('[data-campus-perks-address]')) void this.bindAddressAutocomplete()
    }

    handleAddressKeydown (event) {
      if (!event.target?.matches?.('[data-campus-perks-address]')) return
      // Capture before Google's input listener hides the list on Escape.
      this.addressEscapeEvent = event.key === 'Escape' && this.addressMenu?.getClientRects().length
        && this.windowRef.getComputedStyle(this.addressMenu).visibility !== 'hidden'
        ? event : null
      if (event.key === 'Enter') event.preventDefault()
    }

    async bindAddressAutocomplete () {
      if (!this.connected || this.addressListener || this.addressLoading) return
      const input = this.root.querySelector('[data-campus-perks-address]')
      if (!input) return
      this.addressLoading = true
      const connectionId = this.addressConnectionId
      try {
        await loadAddressPlaces(this.windowRef, this.documentRef)
        if (!this.connected || connectionId !== this.addressConnectionId) return
        // Keep the widget on the same input across theme-editor reconnects.
        if (!this.addressAutocomplete) {
          this.addressExistingMenus = new Set(this.documentRef.querySelectorAll('.pac-container'))
          this.addressAutocomplete = new this.windowRef.google.maps.places.Autocomplete(input, {
            componentRestrictions: { country: ['us'] },
            fields: ['address_components'],
            types: ['address'],
          })
        }
        this.trackAddressMenu()
        this.addressListener = this.addressAutocomplete.addListener('place_changed', () => {
          this.fillProfileAddress(this.addressAutocomplete.getPlace())
        })
      } catch (error) {
        // Manual entry stays available; focusing the street again retries loading.
      } finally {
        if (connectionId === this.addressConnectionId) this.addressLoading = false
      }
    }

    fillProfileAddress (place) {
      if (!place?.address_components?.length) return
      const form = this.root.querySelector('[data-campus-perks-profile-form]')
      if (!form) return
      const part = (type, short = false) => {
        const component = place.address_components.find((item) => item.types.includes(type))
        return component?.[short ? 'short_name' : 'long_name'] || ''
      }
      const address = [part('street_number'), part('route', true)].filter(Boolean).join(' ')
      if (!address) return
      const zip = part('postal_code')
      const suffix = part('postal_code_suffix')
      const values = {
        address,
        city: part('locality') || part('postal_town') || part('sublocality_level_1') || part('sublocality'),
        state: part('administrative_area_level_1', true),
        zipCode: zip && suffix ? `${zip}-${suffix}` : zip,
      }
      for (const [name, value] of Object.entries(values)) {
        const field = form.elements[name]
        if (!field) continue
        field.value = value
        field.setCustomValidity('')
        // A street input event would request predictions again after selection.
        if (name === 'address') continue
        field.dispatchEvent(new this.windowRef.Event('input', { bubbles: true }))
        field.dispatchEvent(new this.windowRef.Event('change', { bubbles: true }))
      }
      form.elements.apartmentSuiteEtc?.focus()
    }

    trackAddressMenu () {
      if (this.addressMenu) return
      const findMenu = () => {
        this.addressMenu = Array.from(this.documentRef.querySelectorAll('.pac-container'))
          .find((menu) => !this.addressExistingMenus.has(menu)) || null
        if (!this.addressMenu) return
        this.addressMenu.classList.add('campus-perks-address-options')
        this.addressMenuObserver?.disconnect()
        const input = this.root.querySelector('[data-campus-perks-address]')
        // Include text entered while the Places script/widget was still loading.
        if (this.connected && this.documentRef.activeElement === input && input.value.trim()) {
          input.dispatchEvent(new this.windowRef.Event('input', { bubbles: true }))
        }
      }
      findMenu()
      // Google's widget creates its body-level menu asynchronously.
      if (!this.addressMenu && this.windowRef.MutationObserver) {
        this.addressMenuObserver?.disconnect()
        this.addressMenuObserver = new this.windowRef.MutationObserver(findMenu)
        this.addressMenuObserver.observe(this.documentRef.body, { childList: true })
      }
    }

    hideAddressSuggestions ({ blurInput = false } = {}) {
      if (this.addressMenu) this.addressMenu.style.display = 'none'
      const input = this.root.querySelector('[data-campus-perks-address]')
      if (blurInput && input && this.documentRef?.activeElement === input) input.blur()
    }

    buildProfilePayload (form) {
      const FormDataConstructor = this.windowRef.FormData || globalScope.FormData
      const formData = new FormDataConstructor(form)
      const value = (name) => String(formData.get(name) || '').trim()
      const values = (name) => formData.getAll(name).map((item) => String(item).trim()).filter(Boolean)
      const selectedCollege = this.resolveTypedCollege()
      const schoolData = mapCollegeToApiSchool(selectedCollege)
      const futureCampusPrograms = values('futureCampusPrograms')
      const userId = parseShopifyCustomerId(this.registeredCustomerId || this.root.dataset.userId)
      const userSourceId = getUserSourceId(this.windowRef?.location?.search)

      const profile = {
        firstName: value('firstName'),
        lastName: value('lastName'),
        email: value('email'),
        emailAddress: value('email'),
        address: value('address'),
        apartmentSuiteEtc: value('apartmentSuiteEtc'),
        city: value('city'),
        state: value('state'),
        zipCode: value('zipCode'),
        phone: value('phone'),
        school: schoolData.school,
        schoolOther: schoolData.schoolOther,
        currentAcademicYear: value('currentAcademicYear'),
        campusOrganizations: values('campusOrganizations'),
        campusOrganizationsOther: value('campusOrganizationsOther'),
        nailFrequency: value('nailFrequency'),
        nailReasons: values('nailReasons'),
        nailReasonsOther: value('nailReasonsOther'),
        nailInspirationSources: values('nailInspirationSources'),
        nailInspirationSourcesOther: value('nailInspirationSourcesOther'),
        interestProducts: values('interestProducts'),
        interestProductsOther: value('interestProductsOther'),
        hearAboutProgramSource: value('hearAboutProgramSource'),
        hearAboutProgramSourceOther: value('hearAboutProgramSourceOther'),
        beautyContentCreation: value('beautyContentCreation'),
        futureCampusPrograms,
        futureCampusProgramsOther: value('futureCampusProgramsOther'),
        socialMediaAccount: futureCampusPrograms.length ? value('socialMediaAccount') : '',
        userSourceId,
      }
      if (userId) profile.userId = userId
      return profile
    }

    validateProfile (form) {
      this.updateProfileConditionalFields()
      if (!form.checkValidity()) {
        form.reportValidity()
        return false
      }

      const schoolInput = this.root.querySelector('[data-campus-perks-school]')
      if (!this.resolveTypedCollege()) {
        schoolInput?.setCustomValidity?.('Please select a school from the matching list.')
        schoolInput?.reportValidity?.()
        schoolInput?.focus?.()
        this.showProfileStatus('Please select your school from the matching list.')
        return false
      }
      schoolInput?.setCustomValidity?.('')

      const groupLabels = {
        currentAcademicYear: 'current academic year',
        campusOrganizations: 'campus organization',
        nailFrequency: 'nail frequency',
        nailReasons: 'reason for doing your nails',
        nailInspirationSources: 'nail inspiration source',
        interestProducts: 'product interest',
        hearAboutProgramSource: 'how you heard about the program',
      }
      for (const group of form.querySelectorAll('[data-required-group]')) {
        const name = group.dataset.requiredGroup
        if (!form.querySelector(`input[name="${name}"]:checked`)) {
          this.showProfileStatus(`Please select at least one ${groupLabels[name] || 'answer'}.`)
          group.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
          group.querySelector('input')?.focus?.()
          return false
        }
      }

      const profile = this.buildProfilePayload(form)
      const missingOtherFields = getMissingOtherFields(profile)
      if (missingOtherFields.length) {
        const input = form.elements?.[missingOtherFields[0]]
        this.showProfileStatus('Please complete the additional details for your Other selection.')
        input?.focus?.()
        return false
      }
      this.showProfileStatus('')
      return true
    }

    showProfileStatus (message) {
      const status = this.root.querySelector('[data-campus-perks-profile-status]')
      if (!status) return
      status.textContent = message
      status.hidden = !message
    }

    async submitProfile (form) {
      if (this.profileSubmitting || !this.validateProfile(form)) return
      const submit = this.root.querySelector('[data-campus-perks-profile-submit]')
      this.profileSubmitting = true
      if (submit) {
        submit.disabled = true
        submit.setAttribute('aria-busy', 'true')
        submit.textContent = 'Submitting…'
      }

      try {
        const profile = this.buildProfilePayload(form)
        const payload = await submitCampusProfile(profile, this.windowRef)
        const discountCode = payload.discountCode
        this.showProfileResult(discountCode, payload?.message || 'Your Campus Profile has been submitted.')
      } catch (error) {
        this.showProfileStatus(error?.message || 'Unable to submit your Campus Profile. Please try again.')
      } finally {
        this.profileSubmitting = false
        if (submit && !submit.hidden) {
          submit.disabled = false
          submit.removeAttribute('aria-busy')
          submit.textContent = 'Get My 30% OFF'
        }
      }
    }

    showProfileResult (discountCode, message) {
      const code = this.root.querySelector('[data-campus-perks-profile-result-code]')
      const resultMessage = this.root.querySelector('[data-campus-perks-profile-result-message]')
      if (code) code.textContent = discountCode
      if (resultMessage) resultMessage.textContent = message
      this.showProfileStatus('')
      this.showView('profile-success', '[data-campus-perks-profile-success-view] [data-campus-perks-view-title]')
    }

    close () {
      if (!this.opened) return
      this.hideAddressSuggestions({ blurInput: true })
      this.opened = false
      this.root.hidden = true
      this.root.setAttribute('aria-hidden', 'true')
      if (this.documentRef?.body?.style) this.documentRef.body.style.overflow = this.previousBodyOverflow
      if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus()
    }

    async handleClick (event) {
      const registrationTrigger = event.target?.closest?.('[data-campus-perks-register-trigger]')
      if (registrationTrigger) {
        event.preventDefault?.()
        this.showRegistration()
        return
      }

      const profileTrigger = event.target?.closest?.('[data-campus-perks-profile-trigger]')
      if (profileTrigger) {
        event.preventDefault?.()
        this.showProfile()
        return
      }

      const registerSubmit = event.target?.closest?.('[data-campus-perks-register-submit]')
      if (registerSubmit) {
        event.preventDefault?.()
        await this.registerCustomer()
        return
      }

      const collegeOption = event.target?.closest?.('[data-campus-perks-college-option]')
      if (collegeOption) {
        event.preventDefault?.()
        this.selectCollege(this.visibleCollegeResults.find((college) => String(college.i) === collegeOption.dataset.campusPerksCollegeOption))
        return
      }

      const closeTrigger = event.target?.closest?.('[data-campus-perks-close]')
      if (closeTrigger) {
        event.preventDefault?.()
        this.close()
        return
      }

      if (!event.target?.closest?.('[data-campus-perks-school-combobox]')) this.hideCollegeOptions()

      const copyButton = event.target?.closest?.('[data-campus-perks-copy], [data-campus-perks-success-copy], [data-campus-perks-profile-result-copy]')
      if (!copyButton) return

      event.preventDefault?.()
      let code = this.root.querySelector('[data-campus-perks-code]')?.value || ''
      if (copyButton.matches?.('[data-campus-perks-success-copy]')) code = 'CAMPUSH4LM'
      if (copyButton.matches?.('[data-campus-perks-profile-result-copy]')) code = this.root.querySelector('[data-campus-perks-profile-result-code]')?.textContent?.trim() || ''
      if (!code) return

      const copied = await copyText(code, this.documentRef, this.navigatorRef)
      if (!copied) return
      copyButton.textContent = copyButton.dataset.copiedText || 'Copied!'
      this.setTimeoutRef?.(() => {
        copyButton.textContent = copyButton.dataset.copyText || 'Copy Code'
      }, 2000)
    }

    handleInput (event) {
      if (!event.target?.matches?.('[data-campus-perks-school]')) return
      const query = event.target.value.trim()
      if (!this.selectedCollege || event.target.value !== this.selectedCollege.n) this.selectedCollege = null
      event.target.setCustomValidity?.('')
      if (!query) {
        this.hideCollegeOptions()
        return
      }
      this.updateSchoolMatches(query)
    }

    handleChange (event) {
      if (event.target?.name === 'campusOrganizations' && event.target.type === 'checkbox') {
        const controls = Array.from(this.root.querySelectorAll('input[name="campusOrganizations"]'))
        const currentValues = controls
          .filter((control) => control !== event.target && control.checked)
          .map((control) => control.value)
        const nextValues = updateExclusiveSelection(currentValues, event.target.value, event.target.checked)
        controls.forEach((control) => {
          control.checked = nextValues.includes(control.value)
        })
      }
      this.updateProfileConditionalFields()
    }

    async handleSubmit (event) {
      if (!event.target?.matches?.('[data-campus-perks-profile-form]')) return
      event.preventDefault?.()
      await this.submitProfile(event.target)
    }

    async handleKeydown (event) {
      if (event === this.addressEscapeEvent) return
      const schoolInput = event.target?.matches?.('[data-campus-perks-school]') ? event.target : null
      if (schoolInput) {
        if (await this.handleSchoolKeydown(event, schoolInput)) return
      }
      if (event.key === 'Tab' && this.opened) {
        const focusable = Array.from(this.root.querySelectorAll(FOCUSABLE_SELECTOR))
          .filter((element) => !element.closest('[hidden]') && element.getClientRects?.().length)
        if (focusable.length) {
          const first = focusable[0]
          const last = focusable[focusable.length - 1]
          if (!focusable.includes(this.documentRef.activeElement)) {
            event.preventDefault?.();
            (event.shiftKey ? last : first).focus()
            return
          }
          if (event.shiftKey && this.documentRef.activeElement === first) {
            event.preventDefault?.()
            last.focus()
            return
          }
          if (!event.shiftKey && this.documentRef.activeElement === last) {
            event.preventDefault?.()
            first.focus()
            return
          }
        }
      }
      if (event.key === 'Escape' && this.opened) this.close()
    }
  }

  const exportsObject = {
    CampusPerksPopupController,
    copyText,
    createStorefrontCustomer,
    getMissingOtherFields,
    loginCampusPerksCustomer,
    loadAddressPlaces,
    mapCollegeToApiSchool,
    matchesCampaign,
    matchesRegistrationSuccess,
    searchColleges,
    saveCampusPerksActivityUser,
    storefrontConfig,
    subscribeCampusPerksCustomer,
    submitCampusProfile,
    updateExclusiveSelection,
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = exportsObject
  if (globalScope) globalScope.CampusPerksPopupController = CampusPerksPopupController

  if (globalScope?.customElements && globalScope.HTMLElement && !globalScope.customElements.get('campus-perks-popup')) {
    class CampusPerksPopupElement extends globalScope.HTMLElement {
      connectedCallback () {
        if (!this.controller) this.controller = new CampusPerksPopupController(this)
        this.controller.connect()
      }

      disconnectedCallback () {
        this.controller?.disconnect()
      }
    }

    globalScope.customElements.define('campus-perks-popup', CampusPerksPopupElement)
  }
})(typeof window !== 'undefined' ? window : globalThis)
